import React from 'react'

import { AppRoutes, LayoutType, type RouteConfig } from '../routes.types'

// Auth-related page components
const LazyLoginPage = React.lazy(() =>
    import('../../pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })),
)
const LazySignupPage = React.lazy(() =>
    import('../../pages/auth/SignupPage/index').then((module) => ({ default: module.SignupPage })),
)
const LazyMagicLinkSetup = React.lazy(() =>
    import('../../pages/auth/MagicLinkSetupPage').then((module) => ({ default: module.MagicLinkSetupPage })),
)
const LazyMagicLinkSigninPage = React.lazy(() =>
    import('../../pages/auth/MagicLinkSigninPage').then((module) => ({ default: module.MagicLinkSigninPage })),
)
const LazyMagicLinkInvitePage = React.lazy(() =>
    import('../../pages/auth/MagicLinkInvitePage').then((module) => ({ default: module.MagicLinkInvitePage })),
)
const LazyMagicLinkSent = React.lazy(() =>
    import('../../pages/auth/MagicLinkSentPage').then((module) => ({ default: module.MagicLinkSentPage })),
)
const LazyMagicLinkExpired = React.lazy(() =>
    import('../../pages/auth/MagicLinkExpiredPage').then((module) => ({ default: module.MagicLinkExpiredPage })),
)

// Employee Intake Survey wrapper
const LazyIntakeSurveyWrapper = React.lazy(() =>
    import('../../components/common/IntakeSurveyWrapper').then((module) => ({
        default: module.IntakeSurveyWrapper,
    })),
)

// Error pages
const LazyForbiddenPage = React.lazy(() =>
    import('../../pages/errors/ForbiddenPage').then((module) => ({ default: module.ForbiddenPage })),
)
const LazyNotFoundPage = React.lazy(() =>
    import('../../pages/errors/NotFoundPage').then((module) => ({ default: module.NotFoundPage })),
)
const LazySurveyExpiredPage = React.lazy(() =>
    import('../../pages/errors/SurveyExpiredPage').then((module) => ({ default: module.SurveyExpiredPage })),
)
const LazyErrorPage = React.lazy(() =>
    import('../../pages/errors/ErrorPage').then((module) => ({ default: module.ErrorPage })),
)
const LazyImpersonationExchangePage = React.lazy(() =>
    import('../../pages/auth/ImpersonationExchangePage').then((module) => ({
        default: module.ImpersonationExchangePage,
    })),
)
const LazySentryTestPage = React.lazy(() =>
    import('../../pages/debug/SentryTestPage').then((module) => ({ default: module.SentryTestPage })),
)

// Public routes (includes hidden internal debug routes)
export const publicRoutes: RouteConfig[] = [
    // Authentication routes
    {
        path: AppRoutes.LOGIN,
        component: LazyLoginPage,
        layout: LayoutType.AUTH,
        requireAuth: false,
        title: 'Sign In',
        description: 'Sign in to your account',
    },
    {
        path: AppRoutes.SIGNUP,
        component: LazySignupPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Create Organization',
        description: 'Create a new organization account',
    },
    {
        path: AppRoutes.COMPLETE,
        component: LazyMagicLinkSetup,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Completing Authentication',
        description: 'Completing your login process',
    },
    {
        path: AppRoutes.SETUP,
        component: LazyMagicLinkSetup,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Setting Up Authentication',
        description: 'Setting up your authentication',
    },
    {
        path: AppRoutes.SIGNIN,
        component: LazyMagicLinkSigninPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Signing In',
        description: 'Signing you into your account',
    },
    {
        path: AppRoutes.INVITE,
        component: LazyMagicLinkInvitePage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Joining Organization',
        description: 'Completing your invitation to join the organization',
    },
    {
        path: AppRoutes.MAGIC_LINK_SENT,
        component: LazyMagicLinkSent,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Magic Link Sent',
        description: 'Check your email for the magic link to sign in',
    },
    {
        path: AppRoutes.MAGIC_LINK_EXPIRED,
        component: LazyMagicLinkExpired,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Magic Link Expired',
        description: 'This magic link has expired',
    },

    // Employee Intake Survey route (REST-compliant with step parameter)
    {
        path: AppRoutes.INTAKE_SURVEY,
        component: LazyIntakeSurveyWrapper,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Employee Intake Survey',
        description: 'Complete your employee onboarding survey',
    },
    // Impersonation exchange route
    {
        path: AppRoutes.IMPERSONATE,
        component: LazyImpersonationExchangePage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Impersonation',
        description: 'Starting impersonation session',
    },
    {
        path: AppRoutes.INTAKE_SURVEY_EXPIRED,
        component: LazySurveyExpiredPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Survey Link Expired',
        description: 'This survey link has expired',
    },

    // Error pages
    {
        path: AppRoutes.FORBIDDEN,
        component: LazyForbiddenPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Access Forbidden',
        description: 'You do not have permission to access this page',
    },
    {
        path: AppRoutes.NOT_FOUND,
        component: LazyNotFoundPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Page Not Found',
        description: 'The page you are looking for does not exist',
    },
    {
        path: AppRoutes.ERROR,
        component: LazyErrorPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Error',
        description: 'An unexpected error occurred',
    },
    // Additional error route for backend redirects that use /auth/error path
    {
        path: '/auth/error' as AppRoutes,
        component: LazyErrorPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Error',
        description: 'An unexpected error occurred',
    },
    // Hidden Sentry test route for staging/preview validation.
    // Note: The page self-disables in production; we keep the route available
    // to simplify verification flows across environments.
    {
        path: AppRoutes.SENTRY_TEST,
        component: LazySentryTestPage,
        layout: LayoutType.MINIMAL,
        requireAuth: false,
        title: 'Sentry Test',
        description: 'Trigger and verify Sentry events',
    },
]
